import React, { ReactNode, useCallback, useMemo } from "react";
import { useUserback } from "@userback/react";
import { AppConfig } from "@/infrastructure/services/config";
import { useUser, useWorkspace } from "@/hooks";
import { FeedbackContext } from "./feedback.context";

const appConfig = AppConfig.getInstance();

export const UserbackFeedbackProvider: React.FC<{ children?: ReactNode }> = ({ children }) => {
    const { user } = useUser();

    const emptyOpenFeedback = useCallback(() => { }, []);
    const emptyContextValue = useMemo(() => ({ openFeedback: emptyOpenFeedback }), [emptyOpenFeedback]);

    if (!user) {
        return (
            <FeedbackContext.Provider value={emptyContextValue}>
                {children}
            </FeedbackContext.Provider>
        );
    }

    return <UserbackFeedbackProviderInner>{children}</UserbackFeedbackProviderInner>;
};

const UserbackFeedbackProviderInner: React.FC<{ children?: ReactNode }> = ({ children }) => {
    const { user } = useUser();
    const { workspace, userRoleNames } = useWorkspace();
    const { open, identify } = useUserback();
    const environment = appConfig.ENV;

    const openFeedback = useCallback(() => {
        identify(user?.email ?? "", {
            name: user?.fullName,
            email: user?.email,
            roles: userRoleNames?.join(", "),
            organizationName: workspace?.organizationName,
            brandName: workspace?.workspaceName,
            environment: environment,
        });

        open("general");

        setTimeout(() => {
            const userbackButtonContainer = document.getElementById("userback_button_container");
            if (userbackButtonContainer) {
                userbackButtonContainer.dataset.reactAriaTopLayer = "true";
            }
        }, 100);
    }, [identify, user, userRoleNames, workspace, environment, open]);

    const contextValue = useMemo(() => ({ openFeedback }), [openFeedback]);

    return (
        <FeedbackContext.Provider value={contextValue}>
            {children}
        </FeedbackContext.Provider>
    );
};
